import { CopyId } from "@/components/copy-id";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  type CreateTierPayload,
  type Event,
  type EventTierPlan,
  eventsProvider,
} from "@/providers/events.provider";
import {
  IconBolt,
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight,
  IconLayersIntersect,
  IconLoader2,
  IconMapPin,
  IconPhoto,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSparkles,
  IconTrash,
  IconUpload,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<Event["status"], string> = {
  draft: "bg-muted text-muted-foreground",
  published:
    "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
  completed: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
};

function StatusBadge({ status }: { status: Event["status"] }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatBRL(centavos: number) {
  return (centavos / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

// ── Event detail sheet ────────────────────────────────────────────────────────

const MAX_BANNER_BYTES = 500 * 1024; // 500 KB

function BannerUpload({
  event,
  onUploaded,
}: {
  event: Event;
  onUploaded: () => void;
}) {
  const [bannerUrl, setBannerUrl] = useState<string | null>(
    event.banner_obj_url,
  );
  const [optimisticPreview, setOptimisticPreview] = useState<string | null>(
    null,
  );

  const uploadMutation = useMutation({
    mutationFn: (base64: string) =>
      eventsProvider.uploadBanner(event.event_id, base64),
    onSuccess: async () => {
      const updated = await eventsProvider.getById(event.event_id);
      setBannerUrl(updated.banner_obj_url);
      setOptimisticPreview(null);
      toast.success("Banner uploaded");
      onUploaded();
    },
    onError: (err: Error) => {
      setOptimisticPreview(null);
      toast.error(err.message);
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only JPEG and PNG images are supported");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_BANNER_BYTES) {
      toast.error("Image must be 500 KB or smaller");
      e.target.value = "";
      return;
    }

    const inputEl = e.target;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(",")[1];
      setOptimisticPreview(dataUrl);
      uploadMutation.mutate(base64);
      inputEl.value = "";
    };
    reader.onerror = () => {
      toast.error("Failed to read file");
      inputEl.value = "";
    };
    reader.readAsDataURL(file);
  }

  const displaySrc = optimisticPreview ?? bannerUrl;
  const canUpload = event.status === "draft" || event.status === "published";

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs text-muted-foreground">Banner</p>
      <label
        className={`relative rounded-lg overflow-hidden border bg-muted/40 block group ${canUpload && !uploadMutation.isPending ? "cursor-pointer" : "cursor-default"}`}
        style={{ aspectRatio: "16/7" }}
      >
        {displaySrc ? (
          <img
            src={displaySrc}
            alt="Event banner"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <IconPhoto className="size-8 text-muted-foreground/40" />
          </div>
        )}
        {canUpload && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
            {uploadMutation.isPending ? (
              <IconLoader2 className="size-6 text-white animate-spin" />
            ) : (
              <div className="flex flex-col items-center gap-1 text-white">
                <IconUpload className="size-5" />
                <span className="text-xs font-medium">
                  {displaySrc ? "Replace" : "Upload"} banner
                </span>
              </div>
            )}
          </div>
        )}
        {canUpload && (
          <input
            type="file"
            accept="image/jpeg,image/png"
            className="sr-only"
            onChange={handleFileChange}
            disabled={uploadMutation.isPending}
          />
        )}
      </label>
      {canUpload && (
        <p className="text-xs text-muted-foreground">JPEG or PNG, max 500 KB</p>
      )}
    </div>
  );
}

function EventDetailSheet({
  event,
  onClose,
  onDelete,
}: {
  event: Event;
  onClose: () => void;
  onDelete: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [detailTab, setDetailTab] = useState<
    "info" | "participants" | "boosts"
  >("info");

  const { data: participants, isLoading: loadingParticipants } = useQuery({
    queryKey: ["event-participants", event.event_id],
    queryFn: () => eventsProvider.listParticipants(event.event_id),
  });

  const { data: boosts, isLoading: loadingBoosts } = useQuery({
    queryKey: ["event-boosts", event.event_id],
    queryFn: () => eventsProvider.listBoosts(event.event_id),
  });

  const totalBoostPoints =
    boosts?.reduce((sum, b) => sum + b.points_spent, 0) ?? 0;

  return (
    <>
      <SheetHeader>
        <SheetTitle className="leading-snug pr-4">{event.title}</SheetTitle>
        <SheetDescription className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={event.status} />
          <span className="text-xs capitalize">· {event.tier_name}</span>
        </SheetDescription>
      </SheetHeader>

      {/* Mini tab bar */}
      <div className="flex border-b px-4">
        {(["info", "participants", "boosts"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setDetailTab(tab)}
            className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium capitalize transition-colors ${
              detailTab === tab
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab}
            {tab === "participants" && participants && (
              <span className="ml-1 rounded-full bg-muted-foreground/15 px-1.5 py-px tabular-nums">
                {participants.length}
              </span>
            )}
            {tab === "boosts" && boosts && boosts.length > 0 && (
              <span className="ml-1 rounded-full bg-muted-foreground/15 px-1.5 py-px tabular-nums">
                {boosts.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {/* Info tab */}
        {detailTab === "info" && (
          <div className="flex flex-col gap-3 text-sm">
            <BannerUpload
              event={event}
              onUploaded={() =>
                queryClient.invalidateQueries({ queryKey: ["admin-events"] })
              }
            />
            {event.description && (
              <p className="text-muted-foreground">{event.description}</p>
            )}
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 rounded-lg border p-3">
              <div>
                <p className="text-xs text-muted-foreground">Starts</p>
                <p className="font-medium">{formatDateTime(event.starts_at)}</p>
              </div>
              {event.ends_at && (
                <div>
                  <p className="text-xs text-muted-foreground">Ends</p>
                  <p className="font-medium">{formatDateTime(event.ends_at)}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground">Boost score</p>
                <p className="font-medium tabular-nums">
                  {event.boost_score.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="font-medium">{formatDate(event.created_at)}</p>
              </div>
              {event.location_label && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Location</p>
                  <p className="font-medium flex items-center gap-1">
                    <IconMapPin className="size-3.5 shrink-0" />
                    {event.location_label}
                    {event.location_lat != null &&
                      event.location_lng != null && (
                        <span className="text-xs text-muted-foreground font-normal">
                          ({event.location_lat.toFixed(4)},{" "}
                          {event.location_lng.toFixed(4)})
                        </span>
                      )}
                  </p>
                </div>
              )}
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground mb-1">Creator</p>
              <p className="text-sm font-medium">{event.created_by_username}</p>
              <p className="font-mono text-xs text-muted-foreground break-all">
                {event.created_by}
              </p>
              {event.club_id && (
                <>
                  <p className="text-xs text-muted-foreground mt-2 mb-1">
                    Club
                  </p>
                  {event.club_name && (
                    <p className="text-sm font-medium">{event.club_name}</p>
                  )}
                  <p className="font-mono text-xs text-muted-foreground break-all">
                    {event.club_id}
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Participants tab */}
        {detailTab === "participants" && (
          <div className="flex flex-col gap-2">
            {loadingParticipants ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-lg" />
              ))
            ) : !participants?.length ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
                <IconUsers className="size-8 opacity-40" />
                <p className="text-sm">No participants yet.</p>
              </div>
            ) : (
              <>
                {participants.map((p) => (
                  <div
                    key={p.user_id}
                    className="flex flex-col gap-2 rounded-lg border px-3 py-2"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium">{p.username}</p>
                        <p className="font-mono text-xs text-muted-foreground">
                          {p.user_id}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(p.joined_at)}
                      </p>
                    </div>
                    {p.vehicle_id && (
                      <div className="flex items-center gap-3">
                        {p.vehicle_photo_url && (
                          <img
                            src={p.vehicle_photo_url}
                            alt={p.vehicle_nickname || "vehicle"}
                            className="h-12 w-20 rounded object-cover"
                          />
                        )}
                        <div className="flex flex-col gap-0.5">
                          <p className="text-xs font-medium">
                            {p.vehicle_nickname ||
                              `${p.vehicle_make} ${p.vehicle_model}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {[p.vehicle_make, p.vehicle_model]
                              .filter(Boolean)
                              .join(" ")}
                            {p.vehicle_type && ` · ${p.vehicle_type}`}
                            {p.vehicle_horsepower &&
                              ` · ${p.vehicle_horsepower}hp`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {[
                              p.vehicle_drive,
                              p.vehicle_transmission === "A"
                                ? "Auto"
                                : p.vehicle_transmission === "M"
                                  ? "Manual"
                                  : null,
                              p.vehicle_engine_position
                                ? `${p.vehicle_engine_position}-engine`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Boosts tab */}
        {detailTab === "boosts" && (
          <div className="flex flex-col gap-2">
            {loadingBoosts ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-lg" />
              ))
            ) : !boosts?.length ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
                <IconBolt className="size-8 opacity-40" />
                <p className="text-sm">No boosts yet.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                  <span className="text-muted-foreground">
                    Total points spent
                  </span>
                  <span className="font-semibold tabular-nums">
                    {totalBoostPoints.toLocaleString()}
                  </span>
                </div>
                {boosts.map((b) => (
                  <div
                    key={b.boost_id}
                    className="flex items-center justify-between rounded-lg border px-3 py-2"
                  >
                    <div>
                      <p className="font-mono text-xs">{b.user_id}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(b.created_at)}
                      </p>
                    </div>
                    <span className="text-sm font-medium tabular-nums">
                      +{b.points_spent.toLocaleString()} pts
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      <SheetFooter className="flex-col gap-2 sm:flex-col">
        <Button
          variant="destructive"
          className="w-full gap-1.5"
          onClick={() => {
            onDelete(event.event_id);
            onClose();
          }}
        >
          <IconTrash className="size-4" />
          Delete event
        </Button>
        <Button variant="outline" className="w-full" onClick={onClose}>
          Close
        </Button>
      </SheetFooter>
    </>
  );
}

// ── Events tab ────────────────────────────────────────────────────────────────

function EventsTab({ tiers }: { tiers: EventTierPlan[] }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const isSearching = searchQuery.trim().length > 0;
  const queryKey = isSearching
    ? ["events-search", searchQuery, page]
    : ["admin-events", page];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () =>
      isSearching
        ? eventsProvider.searchText(searchQuery, page)
        : eventsProvider.adminList(page),
  });

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (searchQuery !== searchInput) setPage(0);
    setSearchQuery(searchInput);
  }

  function handleClearSearch() {
    setSearchInput("");
    setSearchQuery("");
    setPage(0);
  }

  const events = data?.data;
  const hasNext = data?.has_next ?? false;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => eventsProvider.adminDelete(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<{ data: Event[]; has_next: boolean }>(
        queryKey,
        (prev) =>
          prev
            ? { ...prev, data: prev.data.filter((e) => e.event_id !== id) }
            : prev,
      );
      toast.success("Event deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function handleDelete(id: string) {
    if (!confirm("Hard-delete this event? This cannot be undone.")) return;
    deleteMutation.mutate(id);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <form
          onSubmit={handleSearch}
          className="flex items-center gap-2 flex-1"
        >
          <div className="relative flex-1 max-w-sm">
            <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by title or club…"
              className="pl-8 h-8 text-sm"
            />
          </div>
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            className="gap-1.5"
          >
            <IconSearch className="size-3.5" />
            Search
          </Button>
          {isSearching && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1.5"
              onClick={handleClearSearch}
            >
              <IconX className="size-3.5" />
              Clear
            </Button>
          )}
        </form>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => queryClient.invalidateQueries({ queryKey })}
        >
          <IconRefresh className="size-3.5" />
          Refresh
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Event ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Starts</TableHead>
              <TableHead className="text-right">Boost</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-muted-foreground"
                >
                  Failed to load events. Check your connection and try again.
                </TableCell>
              </TableRow>
            ) : !events?.length ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-muted-foreground"
                >
                  {page > 0
                    ? "No more events on this page."
                    : "No events found."}
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => {
                return (
                  <TableRow
                    key={event.event_id}
                    className="cursor-pointer"
                    onClick={() => setSelectedEvent(event)}
                  >
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {event.title}
                    </TableCell>
                    <TableCell>
                      <CopyId id={event.event_id} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={event.status} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground capitalize">
                      {event.tier_name}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(event.starts_at)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {event.boost_score > 0 ? (
                        event.boost_score.toLocaleString()
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8 text-destructive hover:text-destructive"
                        disabled={deleteMutation.isPending}
                        title="Delete event"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(event.event_id);
                        }}
                      >
                        <IconTrash className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {(page > 0 || hasNext) && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="gap-1"
          >
            <IconChevronLeft className="size-3.5" />
            Prev
          </Button>
          <span className="text-xs text-muted-foreground">Page {page + 1}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1"
          >
            Next
            <IconChevronRight className="size-3.5" />
          </Button>
        </div>
      )}

      <Sheet
        open={selectedEvent !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedEvent(null);
        }}
      >
        <SheetContent
          side="right"
          className="sm:max-w-md w-full flex flex-col"
          onFocusOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          {selectedEvent && (
            <EventDetailSheet
              event={selectedEvent}
              onClose={() => setSelectedEvent(null)}
              onDelete={(id) => {
                handleDelete(id);
                setSelectedEvent(null);
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ── Create tier sheet ─────────────────────────────────────────────────────────

const BOOLEAN_FEATURES: { key: keyof CreateTierPayload; label: string }[] = [
  { key: "has_player_checkin", label: "Player check-in" },
  { key: "has_badge", label: "Badge" },
  { key: "has_route_builder", label: "Route builder" },
  { key: "is_visible_on_map", label: "Visible on map" },
  { key: "is_discoverable", label: "Discoverable" },
  { key: "is_advertisable", label: "Advertisable" },
  { key: "is_featured", label: "Featured" },
  { key: "has_analytics", label: "Analytics" },
];

// ── Tier confirm view ─────────────────────────────────────────────────────────

const SCALAR_TIER_FIELDS: {
  key: keyof CreateTierPayload;
  label: string;
  format?: (v: unknown) => string;
}[] = [
  { key: "tier_number", label: "Tier number" },
  { key: "tier_name", label: "Name" },
  {
    key: "price_amount",
    label: "Price",
    format: (v) =>
      v === 0
        ? "Free"
        : ((v as number) / 100).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          }),
  },
  { key: "price_currency", label: "Currency" },
  { key: "minimum_boost", label: "Min boost" },
  { key: "points_multiplier", label: "Multiplier", format: (v) => `${v}×` },
];

function TierConfirmView({
  payload,
  existingTier,
  isPending,
  onBack,
  onConfirm,
}: {
  payload: CreateTierPayload;
  existingTier?: EventTierPlan;
  isPending: boolean;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const isEditing = !!existingTier;

  type ChangeRow =
    | {
        kind: "scalar";
        label: string;
        oldVal?: string;
        newVal: string;
        changed: boolean;
      }
    | {
        kind: "color";
        label: string;
        oldHex?: string;
        newHex: string;
        changed: boolean;
      }
    | {
        kind: "feature";
        label: string;
        oldVal?: boolean;
        newVal: boolean;
        changed: boolean;
      };

  const rows: ChangeRow[] = [];

  // Scalar fields
  for (const { key, label, format } of SCALAR_TIER_FIELDS) {
    const newVal = format ? format(payload[key]) : String(payload[key] ?? "");
    if (isEditing) {
      const rawOld = existingTier[key as keyof EventTierPlan];
      const oldVal = format ? format(rawOld) : String(rawOld ?? "");
      rows.push({
        kind: "scalar",
        label,
        oldVal,
        newVal,
        changed: oldVal !== newVal,
      });
    } else {
      rows.push({ kind: "scalar", label, newVal, changed: false });
    }
  }

  // Color fields
  for (const { key, label } of [
    { key: "primary_color_hex_code" as const, label: "Primary color" },
    { key: "secondary_color_hex_code" as const, label: "Secondary color" },
  ]) {
    const newHex = payload[key] as string;
    if (isEditing) {
      const oldHex = existingTier[key as keyof EventTierPlan] as string;
      rows.push({
        kind: "color",
        label,
        oldHex,
        newHex,
        changed: oldHex !== newHex,
      });
    } else {
      rows.push({ kind: "color", label, newHex, changed: false });
    }
  }

  // Feature flags
  for (const { key, label } of BOOLEAN_FEATURES) {
    const newVal = (payload[key] as boolean) ?? false;
    if (isEditing) {
      const oldVal = existingTier[key as keyof EventTierPlan] as boolean;
      rows.push({
        kind: "feature",
        label,
        oldVal,
        newVal,
        changed: oldVal !== newVal,
      });
    } else {
      rows.push({ kind: "feature", label, newVal, changed: false });
    }
  }

  const changedCount = rows.filter((r) => r.changed).length;

  return (
    <>
      <SheetHeader>
        <SheetTitle>
          {isEditing ? "Confirm changes" : "Confirm new tier"}
        </SheetTitle>
        <SheetDescription>
          {isEditing
            ? changedCount === 0
              ? "No fields have changed."
              : `${changedCount} field${changedCount !== 1 ? "s" : ""} will be updated.`
            : "Review the tier before creating it."}
        </SheetDescription>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-1">
        {rows.map((row) => {
          const highlight = isEditing && row.changed;
          return (
            <div
              key={row.label}
              className={`flex items-start justify-between gap-4 rounded-lg px-3 py-2 text-sm ${
                highlight ? "bg-amber-50 dark:bg-amber-900/20" : ""
              }`}
            >
              <span
                className={`text-xs shrink-0 mt-0.5 ${highlight ? "text-amber-700 dark:text-amber-400 font-medium" : "text-muted-foreground"}`}
              >
                {row.label}
              </span>

              <div className="flex items-center gap-2 text-right flex-wrap justify-end">
                {/* Old value (edit mode only) */}
                {isEditing && row.kind === "scalar" && (
                  <span
                    className={`tabular-nums ${row.changed ? "line-through text-muted-foreground text-xs" : "font-medium"}`}
                  >
                    {row.oldVal}
                  </span>
                )}
                {isEditing && row.kind === "color" && (
                  <div className="flex items-center gap-1">
                    <div
                      className={`size-4 rounded border ${row.changed ? "opacity-40" : ""}`}
                      style={{ backgroundColor: row.oldHex }}
                    />
                    <span
                      className={`font-mono text-xs ${row.changed ? "line-through text-muted-foreground" : "font-medium"}`}
                    >
                      {row.oldHex}
                    </span>
                  </div>
                )}
                {isEditing && row.kind === "feature" && !row.changed && (
                  <span className="font-medium">
                    {row.oldVal ? "Enabled" : "Disabled"}
                  </span>
                )}

                {/* Arrow when changed */}
                {row.changed && (
                  <span className="text-muted-foreground text-xs">→</span>
                )}

                {/* New value */}
                {row.kind === "scalar" && (row.changed || !isEditing) && (
                  <span
                    className={`tabular-nums font-medium ${highlight ? "text-amber-700 dark:text-amber-300" : ""}`}
                  >
                    {row.newVal}
                  </span>
                )}
                {row.kind === "color" && (row.changed || !isEditing) && (
                  <div className="flex items-center gap-1">
                    <div
                      className="size-4 rounded border"
                      style={{ backgroundColor: row.newHex }}
                    />
                    <span
                      className={`font-mono text-xs font-medium ${highlight ? "text-amber-700 dark:text-amber-300" : ""}`}
                    >
                      {row.newHex}
                    </span>
                  </div>
                )}
                {row.kind === "feature" && row.changed && (
                  <span
                    className={`font-medium ${highlight ? "text-amber-700 dark:text-amber-300" : ""}`}
                  >
                    {row.newVal ? "Enabled" : "Disabled"}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <SheetFooter className="flex-col gap-2 sm:flex-col">
        <Button
          onClick={onConfirm}
          disabled={isPending || (isEditing && changedCount === 0)}
          className="w-full"
        >
          {isPending
            ? isEditing
              ? "Updating…"
              : "Creating…"
            : isEditing
              ? "Confirm update"
              : "Confirm & create"}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full gap-1.5"
          onClick={onBack}
          disabled={isPending}
        >
          <IconChevronLeft className="size-3.5" />
          Back to edit
        </Button>
      </SheetFooter>
    </>
  );
}

// ── Create / edit tier sheet ──────────────────────────────────────────────────

function CreateTierSheet({
  onClose,
  tier,
}: {
  onClose: () => void;
  tier?: EventTierPlan;
}) {
  const queryClient = useQueryClient();
  const isEditing = !!tier;

  const [tierNumber, setTierNumber] = useState(
    tier?.tier_number.toString() ?? "",
  );
  const [name, setName] = useState(tier?.tier_name ?? "");
  const [priceAmount, setPriceAmount] = useState(
    tier ? (tier.price_amount / 100).toString() : "",
  );
  const [currency, setCurrency] = useState(tier?.price_currency ?? "BRL");
  const [minimumBoost, setMinimumBoost] = useState(
    tier?.minimum_boost.toString() ?? "",
  );
  const [multiplier, setMultiplier] = useState(
    tier?.points_multiplier.toString() ?? "1",
  );
  const [primaryColor, setPrimaryColor] = useState(
    tier?.primary_color_hex_code ?? "#808080",
  );
  const [secondaryColor, setSecondaryColor] = useState(
    tier?.secondary_color_hex_code ?? "#FFFFFF",
  );
  const [features, setFeatures] = useState<Record<string, boolean>>(
    tier
      ? {
          has_player_checkin: tier.has_player_checkin,
          has_badge: tier.has_badge,
          has_route_builder: tier.has_route_builder,
          is_visible_on_map: tier.is_visible_on_map,
          is_discoverable: tier.is_discoverable,
          is_advertisable: tier.is_advertisable,
          is_featured: tier.is_featured,
          has_analytics: tier.has_analytics,
        }
      : {},
  );

  // When set, the sheet is in "confirm" step
  const [pendingPayload, setPendingPayload] =
    useState<CreateTierPayload | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: CreateTierPayload) =>
      isEditing
        ? eventsProvider.updateTier(tier!.tier_id, payload)
        : eventsProvider.createTier(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-tiers"] });
      toast.success(isEditing ? "Tier updated" : "Tier created");
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message);
      setPendingPayload(null);
    },
  });

  function buildPayload(): CreateTierPayload {
    return {
      tier_number: parseInt(tierNumber, 10),
      tier_name: name,
      price_amount: priceAmount ? Math.round(parseFloat(priceAmount) * 100) : 0,
      price_currency: currency.toUpperCase().slice(0, 3),
      minimum_boost: minimumBoost ? parseInt(minimumBoost, 10) : 0,
      points_multiplier: parseFloat(multiplier) || 1,
      primary_color_hex_code: primaryColor,
      secondary_color_hex_code: secondaryColor,
      ...Object.fromEntries(
        BOOLEAN_FEATURES.map((f) => [f.key, features[f.key] ?? false]),
      ),
    };
  }

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setPendingPayload(buildPayload());
  }

  function toggleFeature(key: string) {
    setFeatures((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  if (pendingPayload) {
    return (
      <TierConfirmView
        payload={pendingPayload}
        existingTier={tier}
        isPending={mutation.isPending}
        onBack={() => setPendingPayload(null)}
        onConfirm={() => mutation.mutate(pendingPayload)}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="contents">
      <SheetHeader>
        <SheetTitle>{isEditing ? "Edit tier" : "Create tier"}</SheetTitle>
        <SheetDescription>
          {isEditing
            ? "Update the tier details and feature set."
            : "Define a new event tier plan and its feature set."}
        </SheetDescription>
      </SheetHeader>

      <div className="flex flex-col gap-4 overflow-y-auto px-4 py-2">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tier-number">Tier number</Label>
            <Input
              id="tier-number"
              type="number"
              min="1"
              step="1"
              placeholder="e.g. 1"
              value={tierNumber}
              onChange={(e) => setTierNumber(e.target.value)}
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tier-name">Name</Label>
            <Input
              id="tier-name"
              placeholder="e.g. premium"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tier-price">Price (R$)</Label>
            <Input
              id="tier-price"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={priceAmount}
              onChange={(e) => setPriceAmount(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Leave blank for free
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tier-currency">Currency</Label>
            <Input
              id="tier-currency"
              maxLength={3}
              placeholder="BRL"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="tier-min-boost">Minimum boost score</Label>
          <Input
            id="tier-min-boost"
            type="number"
            min="0"
            step="1"
            placeholder="0"
            value={minimumBoost}
            onChange={(e) => setMinimumBoost(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Boost threshold required to reach this tier
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="tier-multiplier">Points multiplier</Label>
          <Input
            id="tier-multiplier"
            type="number"
            min="0"
            step="0.1"
            placeholder="1.0"
            value={multiplier}
            onChange={(e) => setMultiplier(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tier-primary-color">Primary color</Label>
            <div className="flex items-center gap-2">
              <Input
                id="tier-primary-color"
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-10 w-14 cursor-pointer"
              />
              <Input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="#808080"
                className="flex-1"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tier-secondary-color">Secondary color</Label>
            <div className="flex items-center gap-2">
              <Input
                id="tier-secondary-color"
                type="color"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="h-10 w-14 cursor-pointer"
              />
              <Input
                type="text"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                placeholder="#FFFFFF"
                className="flex-1"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Features
          </p>
          <div className="grid grid-cols-2 gap-2">
            {BOOLEAN_FEATURES.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => toggleFeature(f.key)}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                  features[f.key]
                    ? "border-primary bg-primary/5 text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <div
                  className={`size-2 rounded-full shrink-0 ${
                    features[f.key] ? "bg-primary" : "bg-muted-foreground/30"
                  }`}
                />
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <SheetFooter>
        <Button type="submit" className="w-full">
          {isEditing ? "Review changes" : "Review & create"}
        </Button>
      </SheetFooter>
    </form>
  );
}

// ── Tiers tab ─────────────────────────────────────────────────────────────────

function TiersTab() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingTier, setEditingTier] = useState<EventTierPlan | null>(null);

  const {
    data: tiers,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["event-tiers"],
    queryFn: () => eventsProvider.listTiers(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => eventsProvider.deleteTier(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<EventTierPlan[]>(
        ["event-tiers"],
        (prev) => prev?.filter((t) => t.tier_id !== id) ?? [],
      );
      toast.success("Tier deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function handleDelete(tier: EventTierPlan) {
    if (
      !confirm(
        `Delete tier "${tier.tier_name}"? Events using it may be affected.`,
      )
    )
      return;
    deleteMutation.mutate(tier.tier_id);
  }

  function FeatureDot({ on }: { on: boolean }) {
    return (
      <span
        className={`inline-block size-2 rounded-full ${on ? "bg-green-500" : "bg-muted-foreground/25"}`}
        title={on ? "enabled" : "disabled"}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["event-tiers"] })
          }
        >
          <IconRefresh className="size-3.5" />
          Refresh
        </Button>
        <Button
          size="sm"
          className="gap-1.5"
          onClick={() => setShowCreate(true)}
        >
          <IconPlus className="size-4" />
          New tier
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Price</TableHead>
              <TableHead className="text-right">Min boost</TableHead>
              <TableHead className="text-center">Check-in</TableHead>
              <TableHead className="text-center">Badge</TableHead>
              <TableHead className="text-center">Routes</TableHead>
              <TableHead className="text-center">Map</TableHead>
              <TableHead className="text-center">Discover</TableHead>
              <TableHead className="text-center">Ads</TableHead>
              <TableHead className="text-center">Featured</TableHead>
              <TableHead className="text-center">Analytics</TableHead>
              <TableHead>Multiplier</TableHead>
              <TableHead>Colors</TableHead>
              <TableHead className="text-center">Enabled</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 16 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={16}
                  className="py-12 text-center text-muted-foreground"
                >
                  Failed to load tiers.
                </TableCell>
              </TableRow>
            ) : !tiers?.length ? (
              <TableRow>
                <TableCell
                  colSpan={16}
                  className="py-12 text-center text-muted-foreground"
                >
                  No tiers yet.{" "}
                  <button
                    className="underline hover:text-foreground"
                    onClick={() => setShowCreate(true)}
                  >
                    Create one
                  </button>
                  .
                </TableCell>
              </TableRow>
            ) : (
              tiers.map((tier) => (
                <TableRow
                  key={tier.tier_id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setEditingTier(tier)}
                >
                  <TableCell className="tabular-nums text-sm text-muted-foreground">
                    {tier.tier_number}
                  </TableCell>
                  <TableCell className="font-medium capitalize">
                    {tier.tier_name}
                  </TableCell>
                  <TableCell className="tabular-nums text-sm">
                    {tier.price_amount === 0 ? (
                      <span className="text-muted-foreground">Free</span>
                    ) : (
                      formatBRL(tier.price_amount)
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums text-sm text-right">
                    {tier.minimum_boost > 0 ? (
                      tier.minimum_boost.toLocaleString()
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <FeatureDot on={tier.has_player_checkin} />
                  </TableCell>
                  <TableCell className="text-center">
                    <FeatureDot on={tier.has_badge} />
                  </TableCell>
                  <TableCell className="text-center">
                    <FeatureDot on={tier.has_route_builder} />
                  </TableCell>
                  <TableCell className="text-center">
                    <FeatureDot on={tier.is_visible_on_map} />
                  </TableCell>
                  <TableCell className="text-center">
                    <FeatureDot on={tier.is_discoverable} />
                  </TableCell>
                  <TableCell className="text-center">
                    <FeatureDot on={tier.is_advertisable} />
                  </TableCell>
                  <TableCell className="text-center">
                    <FeatureDot on={tier.is_featured} />
                  </TableCell>
                  <TableCell className="text-center">
                    <FeatureDot on={tier.has_analytics} />
                  </TableCell>
                  <TableCell className="tabular-nums text-sm">
                    {tier.points_multiplier}×
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-6 w-6 rounded border"
                        style={{ backgroundColor: tier.primary_color_hex_code }}
                        title={tier.primary_color_hex_code}
                      />
                      <div
                        className="h-6 w-6 rounded border"
                        style={{
                          backgroundColor: tier.secondary_color_hex_code,
                        }}
                        title={tier.secondary_color_hex_code}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <FeatureDot on={tier.is_enabled} />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive hover:text-destructive"
                      disabled={deleteMutation.isPending}
                      title="Delete tier"
                      onClick={() => handleDelete(tier)}
                    >
                      <IconTrash className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Sheet
        open={showCreate || !!editingTier}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreate(false);
            setEditingTier(null);
          }
        }}
      >
        <SheetContent side="right" className="sm:max-w-md w-full flex flex-col">
          {(showCreate || editingTier) && (
            <CreateTierSheet
              tier={editingTier ?? undefined}
              onClose={() => {
                setShowCreate(false);
                setEditingTier(null);
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ── Recommendations tab ───────────────────────────────────────────────────────

const PRESET_LOCATIONS = [
  { label: "São Carlos, SP", lat: -22.0087, lng: -47.8909 },
  { label: "São Paulo, SP", lat: -23.5505, lng: -46.6333 },
  { label: "Brasília, DF", lat: -15.8267, lng: -47.9218 },
];

const DEFAULT_WEIGHTS = {
  w_timing: 0.35,
  w_distance: 0.2,
  w_club: 0.2,
  w_tier: 0.15,
  w_participants: 0.1,
};

type WeightKey = keyof typeof DEFAULT_WEIGHTS;

// When one weight changes, scale the remaining three proportionally so sum stays 1.
function normalizeWeights(
  weights: typeof DEFAULT_WEIGHTS,
  changed: WeightKey,
  newVal: number,
) {
  const clamped = Math.min(Math.max(newVal, 0), 1);
  const others = (Object.keys(weights) as WeightKey[]).filter(
    (k) => k !== changed,
  );
  const remainingSum = others.reduce((s, k) => s + weights[k], 0);
  const available = 1 - clamped;
  const next = { ...weights, [changed]: clamped } as typeof DEFAULT_WEIGHTS;
  if (remainingSum === 0) {
    const each = available / others.length;
    others.forEach((k) => {
      next[k] = each;
    });
  } else {
    others.forEach((k) => {
      next[k] = (weights[k] / remainingSum) * available;
    });
  }
  // Round to 2 dp to avoid floating-point drift
  (Object.keys(next) as WeightKey[]).forEach((k) => {
    next[k] = Math.round(next[k] * 100) / 100;
  });
  // Correct rounding error on the last key
  const sum = (Object.values(next) as number[]).reduce((a, b) => a + b, 0);
  const diff = Math.round((1 - sum) * 100) / 100;
  if (diff !== 0) {
    const last = others[others.length - 1];
    next[last] = Math.round((next[last] + diff) * 100) / 100;
  }
  return next;
}

function WeightSlider({
  label,
  name,
  value,
  onChange,
}: {
  label: string;
  name: string;
  value: number;
  onChange: (v: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  function commit(raw: string) {
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) onChange(Math.min(Math.max(parsed, 0), 1));
    setDraft(null);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">{label}</Label>
        <input
          type="text"
          inputMode="decimal"
          value={draft ?? value.toFixed(2)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" && commit((e.target as HTMLInputElement).value)
          }
          className="w-14 font-mono text-xs tabular-nums text-right bg-muted rounded px-1.5 py-0.5 border border-border focus:outline-none focus:border-primary"
        />
      </div>
      <input
        type="range"
        name={name}
        min={0}
        max={1}
        step={0.01}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-primary"
      />
    </div>
  );
}

function RecommendationsTab() {
  const [weightsOn, setWeightsOn] = useState(false);
  const [weights, setWeights] = useState({ ...DEFAULT_WEIGHTS });
  const [locationPreset, setLocationPreset] = useState<string>(
    PRESET_LOCATIONS[0].label,
  );
  const [customLat, setCustomLat] = useState("");
  const [customLng, setCustomLng] = useState("");
  const [page, setPage] = useState(0);
  const [committed, setCommitted] = useState<{
    lat?: number;
    lng?: number;
    w_timing?: number;
    w_distance?: number;
    w_club?: number;
    w_tier?: number;
    w_participants?: number;
    page: number;
  }>({ page: 0 });

  const selectedPreset = PRESET_LOCATIONS.find(
    (p) => p.label === locationPreset,
  );
  const isCustom = locationPreset === "custom";

  const resolvedLat = isCustom ? parseFloat(customLat) : selectedPreset?.lat;
  const resolvedLng = isCustom ? parseFloat(customLng) : selectedPreset?.lng;
  const hasValidLocation =
    resolvedLat !== undefined &&
    resolvedLng !== undefined &&
    !isNaN(resolvedLat) &&
    !isNaN(resolvedLng);

  function buildParams() {
    return {
      page,
      ...(hasValidLocation ? { lat: resolvedLat, lng: resolvedLng } : {}),
      ...(weightsOn ? weights : {}),
    };
  }

  const queryKey = ["events-recommended", committed];
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => eventsProvider.getRecommended(committed),
  });

  function handleFetch() {
    const params = buildParams();
    setCommitted(params);
    setPage(params.page);
  }

  const events = data?.data;
  const hasNext = data?.has_next ?? false;

  return (
    <div className="flex flex-col gap-6">
      {/* Controls */}
      <div className="rounded-lg border p-4 flex flex-col gap-5">
        {/* Location */}
        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium">Location</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Select
              value={locationPreset}
              onValueChange={(v) => {
                setLocationPreset(v);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESET_LOCATIONS.map((p) => (
                  <SelectItem key={p.label} value={p.label}>
                    {p.label}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom…</SelectItem>
              </SelectContent>
            </Select>
            {isCustom ? (
              <>
                <Input
                  placeholder="Lat (e.g. -23.5505)"
                  value={customLat}
                  onChange={(e) => setCustomLat(e.target.value)}
                  className="w-44 font-mono text-sm"
                />
                <Input
                  placeholder="Lng (e.g. -46.6333)"
                  value={customLng}
                  onChange={(e) => setCustomLng(e.target.value)}
                  className="w-44 font-mono text-sm"
                />
              </>
            ) : (
              <span className="font-mono text-xs text-muted-foreground">
                {selectedPreset?.lat}, {selectedPreset?.lng}
              </span>
            )}
          </div>
        </div>

        {/* Weights */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">Override weights</p>
            <button
              type="button"
              onClick={() => setWeightsOn((v) => !v)}
              className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 transition-colors cursor-pointer ${weightsOn ? "bg-primary border-primary" : "bg-transparent border-border"}`}
            >
              <span
                className={`pointer-events-none inline-block size-3.5 rounded-full shadow transition-transform mt-px ${weightsOn ? "translate-x-[18px] bg-primary-foreground" : "translate-x-px bg-muted-foreground"}`}
              />
            </button>
            {weightsOn && (
              <button
                type="button"
                onClick={() => setWeights({ ...DEFAULT_WEIGHTS })}
                className="ml-auto text-xs text-muted-foreground hover:text-foreground"
              >
                Reset to defaults
              </button>
            )}
          </div>
          {weightsOn && (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-y-5">
                {(
                  [
                    {
                      key: "w_timing",
                      label: "Timing",
                      desc: "Favors events happening soon. Events starting within 30 days score higher; distant or past events score zero.",
                    },
                    {
                      key: "w_distance",
                      label: "Distance",
                      desc: "Penalizes far-away events. Score decreases linearly up to 1000 km; beyond that events are excluded. Requires location.",
                    },
                    {
                      key: "w_club",
                      label: "Club",
                      desc: "Boosts events created by a club the user belongs to. Binary — full score on match, zero otherwise.",
                    },
                    {
                      key: "w_tier",
                      label: "Tier",
                      desc: "Favors events with more cumulative boost. Score is proportional to the event's total boost score relative to the highest-boosted event at query time.",
                    },
                    {
                      key: "w_participants",
                      label: "Participants",
                      desc: "Favors events with more attendees. Score increases up to 100 participants, then plateaus.",
                    },
                  ] as { key: WeightKey; label: string; desc: string }[]
                ).map(({ key, label, desc }) => (
                  <div key={key} className="flex flex-col gap-1">
                    <p className="text-xs text-muted-foreground">{desc}</p>
                    <WeightSlider
                      label={`${label} (${key})`}
                      name={key}
                      value={weights[key]}
                      onChange={(v) =>
                        setWeights((w) => normalizeWeights(w, key, v))
                      }
                    />
                  </div>
                ))}
              </div>
              {/* Visual proportion bar */}
              <div className="flex h-4 w-full overflow-hidden rounded-full">
                <div
                  style={{ width: `${weights.w_timing * 100}%` }}
                  className="h-full bg-blue-500 transition-all"
                  title={`Timing ${(weights.w_timing * 100).toFixed(0)}%`}
                />
                <div
                  style={{ width: `${weights.w_distance * 100}%` }}
                  className="h-full bg-green-500 transition-all"
                  title={`Distance ${(weights.w_distance * 100).toFixed(0)}%`}
                />
                <div
                  style={{ width: `${weights.w_club * 100}%` }}
                  className="h-full bg-orange-500 transition-all"
                  title={`Club ${(weights.w_club * 100).toFixed(0)}%`}
                />
                <div
                  style={{ width: `${weights.w_tier * 100}%` }}
                  className="h-full bg-purple-500 transition-all"
                  title={`Tier ${(weights.w_tier * 100).toFixed(0)}%`}
                />
                <div
                  style={{ width: `${weights.w_participants * 100}%` }}
                  className="h-full bg-rose-500 transition-all"
                  title={`Participants ${(weights.w_participants * 100).toFixed(0)}%`}
                />
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1">
                  <span className="inline-block size-2 rounded-full bg-blue-500" />
                  Timing {(weights.w_timing * 100).toFixed(0)}%
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block size-2 rounded-full bg-green-500" />
                  Distance {(weights.w_distance * 100).toFixed(0)}%
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block size-2 rounded-full bg-orange-500" />
                  Club {(weights.w_club * 100).toFixed(0)}%
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block size-2 rounded-full bg-purple-500" />
                  Tier {(weights.w_tier * 100).toFixed(0)}%
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block size-2 rounded-full bg-rose-500" />
                  Participants {(weights.w_participants * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          )}
        </div>

        <Button onClick={handleFetch} className="self-start gap-1.5">
          <IconSparkles className="size-4" />
          Fetch recommendations
        </Button>
      </div>

      {/* Results */}
      {(isLoading || isError || events) && (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Starts</TableHead>
                <TableHead className="text-right">Boost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-12 text-center text-muted-foreground"
                  >
                    Failed to load recommendations.
                  </TableCell>
                </TableRow>
              ) : !events?.length ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-12 text-center text-muted-foreground"
                  >
                    No recommendations returned.
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => {
                  return (
                    <TableRow key={event.event_id}>
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {event.title}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={event.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground capitalize">
                        {event.tier_name}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDate(event.starts_at)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {event.boost_score > 0 ? (
                          event.boost_score.toLocaleString()
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {events && (page > 0 || hasNext) && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => {
              setPage((p) => p - 1);
              setCommitted((c) => ({ ...c, page: c.page - 1 }));
            }}
            className="gap-1"
          >
            <IconChevronLeft className="size-3.5" />
            Prev
          </Button>
          <span className="text-xs text-muted-foreground">Page {page + 1}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => {
              setPage((p) => p + 1);
              setCommitted((c) => ({ ...c, page: c.page + 1 }));
            }}
            className="gap-1"
          >
            Next
            <IconChevronRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function EventsPage() {
  const [activeTab, setActiveTab] = useState("events");

  const { data: tiers = [] } = useQuery({
    queryKey: ["event-tiers"],
    queryFn: () => eventsProvider.listTiers(),
  });

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Events" />

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-col flex-1 overflow-hidden"
      >
        <div className="flex items-center px-4 pt-4 pb-2 lg:px-6 lg:pt-6">
          <TabsList>
            <TabsTrigger value="events" className="gap-1.5">
              <IconCalendarEvent className="size-4" />
              Events
            </TabsTrigger>
            <TabsTrigger value="tiers" className="gap-1.5">
              <IconLayersIntersect className="size-4" />
              Tiers
              {tiers.length > 0 && (
                <span className="rounded-full bg-muted-foreground/15 px-1.5 py-px text-xs tabular-nums">
                  {tiers.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="recommendations" className="gap-1.5">
              <IconSparkles className="size-4" />
              Recommendations
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="events"
          className="flex-1 overflow-auto px-4 pb-4 lg:px-6 lg:pb-6"
        >
          <EventsTab tiers={tiers} />
        </TabsContent>

        <TabsContent
          value="tiers"
          className="flex-1 overflow-auto px-4 pb-4 lg:px-6 lg:pb-6"
        >
          <TiersTab />
        </TabsContent>

        <TabsContent
          value="recommendations"
          className="flex-1 overflow-auto px-4 pb-4 lg:px-6 lg:pb-6"
        >
          <RecommendationsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
